import { useEffect, useState } from "react";

import { Cart, Space } from "@prisma/client";
import dayjs from "dayjs";

import { InfoTooltip } from "./InfoTooltip";

export default function CountDownTimer({
    space,
    cart,
    isToolBar = false,
}: {
    space: Space;
    cart?: Cart;
    isToolBar?: boolean;
}) {
    const isCustomersSpace = !!cart?.spaces.find(
        ({ space: { lotSellUnitId } }) => lotSellUnitId === space.lotSellUnitId,
    );

    const calculateTimeLeft = () => {
        const endDate = dayjs(space.lockedUntil);
        const now = dayjs();
        const difference = endDate.diff(now);

        let timeLeft: {
            hours?: string | number;
            minutes?: string | number;
            seconds?: string | number;
        } = {};

        function getDifference(interval: "hour" | "minute" | "second") {
            const divider = interval === "hour" ? 24 : 60;

            if ((endDate.diff(now, interval) % divider).toString().length === 1) {
                return `0${endDate.diff(now, interval) % divider}`;
            }

            return endDate.diff(now, interval) % divider;
        }

        if (difference > 0) {
            timeLeft = {
                // Might get added later to 24hrs
                // days: endDate.diff(now, 'day'),
                hours: getDifference("hour"),
                minutes: getDifference("minute"),
                seconds: getDifference("second"),
            };
        }

        return timeLeft;
    };

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
        const timer = setTimeout(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearTimeout(timer);
    });

    const timerComponents: JSX.Element[] = [];

    Object.keys(timeLeft).forEach((interval) => {
        if (!timeLeft[interval as keyof typeof timeLeft]) {
            return;
        }

        timerComponents.push(
            <span key={interval}>
                {timeLeft[interval as keyof typeof timeLeft]}
                {interval !== "seconds" && ":"}
            </span>,
        );
    });

    const { hours, minutes } = timeLeft;

    let className = "";
    if (hours === "00") {
        className = "text-primary-dark";
    }
    if (hours === "00" && typeof minutes === "number" && minutes < 10) {
        className = "text-destructive";
    }

    return timerComponents?.length > 0 ? (
        isToolBar ? (
            <ToolBarTimer className={className} timerComponents={timerComponents} />
        ) : (
            <CardTimer className={className} isCustomersSpace={isCustomersSpace} timerComponents={timerComponents} />
        )
    ) : null;
}

function ToolBarTimer({ className, timerComponents }: { className: string; timerComponents: JSX.Element[] }) {
    return (
        <div className="relative flex items-center gap-2">
            <div className={`flex flex-start rounded w-16 ${className}`}>{timerComponents}</div>
            <InfoTooltip content="You have a memorial space temporarily reserved." />
        </div>
    );
}

function CardTimer({
    className,
    isCustomersSpace,
    timerComponents,
}: {
    className: string;
    isCustomersSpace: boolean;
    timerComponents: JSX.Element[];
}) {
    const { title, subtitle } = isCustomersSpace
        ? {
            title: "Your chosen space is reserved.",
            subtitle:
                "We've temporarily held this plot for you while you finalize your decision. Take your time to consider – we understand this is an important choice.",
        }
        : {
            title: "This space is currently reserved.",
            subtitle:
                "We apologize, but another family is considering this plot at the moment. We have similar options available.",
        };

    return (
        <div className="flex flex-col flex-1 justify-between items-start gap-1">
            <div className="">
                <h2 className="font-medium">
                    {title}
                    <span className="ml-2 leading-[30px] align-top">
                        <InfoTooltip content={subtitle} />
                    </span>
                </h2>
            </div>
            {/* TODO: make pill matching space card */}
            <div className={`flex flex-start rounded w-16 ${className}`}>{timerComponents}</div>
        </div>
    );
}
